import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { AttestationConsentComponent } from '@/components/Chat/AttestationConsent'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { useStaffSession } from '@/hooks/useStaffSession'
import { type AttestationConsent, AttestationScope } from '@/models/attestation'
import { confirmApplication, ConfirmInfoRefusal } from '@/models/confirmInfo'
import { blockingChecks, type FillCheck } from '@/models/fillApplication'
import { refreshInsuranceApplication } from '@/models/insuranceApplication'
import type { EditedCard } from './confirmSteps'
import { useConfirmInfoStep } from './useConfirmInfoStep'
import { useSaveEdits } from './useSaveEdits'

interface ReviewCardProps {
  orgId: string
  applicationId: string | undefined
  checks: FillCheck[]
  edited: EditedCard[]
  onConfirmed: () => void
}

/** Cai's certification; confirming needs it accepted and nothing left blocking. */
export function ReviewCard({ orgId, applicationId, checks, edited, onConfirmed }: ReviewCardProps) {
  const staffSession = useStaffSession()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [accepted, setAccepted] = useState(false)
  const [consents, setConsents] = useState<AttestationConsent[]>([])
  const { saveEdits, saving } = useSaveEdits(edited)
  const { run, pending, error, refusedCode } = useConfirmInfoStep(orgId)
  const blocking = blockingChecks(checks)

  // Stable identity: the attestation component has this in an effect's dependencies.
  const handleConsentChange = useCallback((allAccepted: boolean, given: AttestationConsent[]) => {
    setAccepted(allAccepted)
    setConsents(given)
  }, [])

  useEffect(() => {
    const askedInChat = [ConfirmInfoRefusal.PRIOR_CLAIMS, ConfirmInfoRefusal.QUESTIONS_IN_CHAT]
    if (!askedInChat.includes(refusedCode as ConfirmInfoRefusal) || !applicationId) return
    // The chat asks it; /application reads the application, so it has to see the chat moved first.
    const toChat = () => void navigate('/application')
    refreshInsuranceApplication(queryClient, applicationId).then(toChat, toChat)
  }, [refusedCode, applicationId, navigate, queryClient])

  async function confirm() {
    if ((await saveEdits()) && (await run(() => confirmApplication(orgId, consents)))) onConfirmed()
  }

  return (
    // Not a card: this is what the page is for, not another part of the application.
    <section className="flex flex-col gap-4">
      <h2 className="leading-none font-semibold">Confirm and get quotes</h2>
      <AttestationConsentComponent
        orgId={orgId}
        scope={AttestationScope.REVIEW}
        readonly={staffSession}
        onConsentChange={staffSession ? undefined : handleConsentChange}
      />
      <FieldError className="text-xs">{error}</FieldError>
      <div>
        <Button
          type="button"
          className="text-md"
          onClick={() => void confirm()}
          // Unsaved edits may be what fills the gaps, so the server checks once they're saved.
          disabled={staffSession || !accepted || (blocking.length > 0 && edited.length === 0)}
          loading={saving ? 'Saving…' : pending ? 'Confirming…' : undefined}
        >
          {edited.length > 0 ? 'Save and submit application' : 'Submit application'}
        </Button>
      </div>
    </section>
  )
}
